import { BaseHTMLElement, customElement } from 'dom-native';

import { resolveIconDefinition } from '@/utils/icons';

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function normalizeCssSize(size: string | null): string | null {
  if (!size) {
    return null;
  }

  const trimmed = size.trim();
  if (!trimmed) {
    return null;
  }

  return /^-?\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
}

function parseViewBoxSize(viewBox: string): { width: number; height: number } | null {
  const parts = viewBox.trim().split(/\s+/).map((part) => Number.parseFloat(part));
  if (parts.length !== 4 || Number.isNaN(parts[2]) || Number.isNaN(parts[3]) || parts[2] <= 0 || parts[3] <= 0) {
    return null;
  }

  return {
    width: parts[2],
    height: parts[3],
  };
}

@customElement('help-agent-icon')
export class HelpAgentIcon extends BaseHTMLElement {
  static get observedAttributes(): string[] {
    return ['name', 'size', 'width', 'height', 'color', 'label'];
  }

  init(): void {
    super.init();
    this.render();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private render(): void {
    const definition = resolveIconDefinition(this.getAttribute('name'));
    const size = normalizeCssSize(this.getAttribute('size'));
    const width = normalizeCssSize(this.getAttribute('width'));
    const height = normalizeCssSize(this.getAttribute('height'));
    const color = this.getAttribute('color');
    const label = this.getAttribute('label');

    if (size) {
      this.style.setProperty('--ha-icon-size', size);
    } else {
      this.style.removeProperty('--ha-icon-size');
    }

    if (width) {
      this.style.setProperty('--ha-icon-width', width);
    } else {
      this.style.removeProperty('--ha-icon-width');
    }

    if (height) {
      this.style.setProperty('--ha-icon-height', height);
    } else {
      this.style.removeProperty('--ha-icon-height');
    }

    if (color) {
      this.style.color = color;
    } else {
      this.style.removeProperty('color');
    }

    if (!definition) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    this.hidden = false;
    const viewBoxSize = parseViewBoxSize(definition.viewBox);
    if (viewBoxSize) {
      this.style.setProperty('--ha-icon-aspect-ratio', `${viewBoxSize.width} / ${viewBoxSize.height}`);
    } else {
      this.style.removeProperty('--ha-icon-aspect-ratio');
    }

    const a11yAttrs = label
      ? `role="img" aria-label="${escapeAttribute(label)}"`
      : 'aria-hidden="true"';

    this.innerHTML = `
      <svg
        viewBox="${definition.viewBox}"
        fill="currentColor"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
        ${a11yAttrs}
      >
        ${definition.body}
      </svg>
    `;
  }
}
